import { Form, Formik } from "formik";
import { useState } from "react";
import { Alert } from "@volca/ui/components/ui/alert";
import { AutoComplete } from "@volca/ui/components/ui/autocomplete";
import { Button } from "@volca/ui/components/ui/button";
import { Card, CardContent } from "@volca/ui/components/ui/card";
import { Field, FieldError, FieldLabel } from "@volca/ui/components/ui/field";
import { Input } from "@volca/ui/components/ui/input";
import { Spinner } from "@volca/ui/components/ui/spinner";
import { Stack } from "@volca/ui/components/ui/stack";
import type { IEnrichedBusiness } from "@/business/business";
import { extractErrorMessage } from "@/core/api/errors";
import {
  buildCompanyPayload,
  BUSINESS_TIME_ZONE_OPTIONS,
  getInitialBusinessSetupValues,
  validateCompanyStep,
} from "./business-setup";
import { PublishStepPayload } from "./publish-step-payload";
import { useOnboardingNavigation } from "./shared-query";
import { StepLayout } from "./step-layout";

interface CompanyStepScreenProps {
  business: Pick<
    IEnrichedBusiness,
    "id" | "name" | "time_zone_label" | "current_pay_period_template"
  >;
}

export const CompanyStepScreen = ({ business }: CompanyStepScreenProps) => {
  const initialValues = getInitialBusinessSetupValues(business);
  const [timeZoneSearch, setTimeZoneSearch] = useState(initialValues.timeZone);
  const saveMutation = useOnboardingNavigation(business.id);

  return (
    <StepLayout
      activePane="company"
      title="Tell us about your company"
      subtitle="We use these details to set up your selected products."
    >
      <Formik
        initialValues={initialValues}
        validate={validateCompanyStep}
        onSubmit={(values, helpers) => {
          helpers.setStatus(undefined);
          saveMutation.mutate(
            {
              step: "company",
              direction: "next",
              business: buildCompanyPayload(values),
            },
            {
              onSuccess: () => {
                helpers.setSubmitting(false);
              },
              onError: (error) => {
                helpers.setStatus(extractErrorMessage(error).message);
                helpers.setSubmitting(false);
              },
            },
          );
        }}
      >
        {({
          values,
          errors,
          status,
          submitCount,
          isSubmitting,
          setFieldValue,
          setStatus,
        }) => (
          // noValidate: this form shows its own message, so the browser must not
          // pre-empt it with a native bubble on a required field.
          <Form noValidate>
            <PublishStepPayload
              payload={
                Object.keys(validateCompanyStep(values)).length
                  ? undefined
                  : { business: buildCompanyPayload(values) }
              }
            />
            <Stack className="w-full" gap={6} align="stretch">
              <Card className="w-full gap-0 py-0">
                <CardContent className="p-6 sm:p-8">
                  <Stack className="w-full" gap={6} align="stretch">
                    <Field
                      data-invalid={
                        Boolean(submitCount > 0 && errors.name) || undefined
                      }
                    >
                      <FieldLabel htmlFor="company-name" className="gap-0.5">
                        Company name
                        <span aria-hidden="true">*</span>
                      </FieldLabel>
                      <Input
                        id="company-name"
                        name="name"
                        aria-label="Company name"
                        value={values.name}
                        required
                        aria-invalid={
                          submitCount > 0 && errors.name ? true : undefined
                        }
                        onChange={(event) =>
                          setFieldValue("name", event.target.value)
                        }
                      />
                      {submitCount > 0 && errors.name && (
                        <FieldError>{errors.name}</FieldError>
                      )}
                    </Field>

                    <AutoComplete
                      label="Time zone"
                      placeholder="Select a time zone"
                      selectedValue={values.timeZone}
                      onSelectedValueChange={(value) =>
                        setFieldValue("timeZone", value)
                      }
                      searchValue={timeZoneSearch}
                      onSearchValueChange={setTimeZoneSearch}
                      error={submitCount > 0 ? errors.timeZone : undefined}
                      items={BUSINESS_TIME_ZONE_OPTIONS}
                      required
                      fullWidth
                    />

                    {/* The banner is only for a save that failed: every validation
                          message sits on its own field. */}
                    {status && <Alert color="danger">{status}</Alert>}
                  </Stack>
                </CardContent>
              </Card>

              {/* Grouped bottom-right: two buttons pushed to opposite edges read as two
                  stranded controls, a pair reads as one decision. */}
              <Stack
                className="w-full"
                direction="row"
                gap={2}
                justify="end"
                align="center"
              >
                <Button
                  type="button"
                  variant="outline"
                  disabled={isSubmitting || saveMutation.isPending}
                  onClick={() => {
                    setStatus(undefined);
                    saveMutation.mutate(
                      {
                        step: "company",
                        direction: "back",
                        // Back is leaving the step, so it carries what Continue would.
                        // Sending only the move meant valid edits survived while the
                        // component stayed mounted and were gone after a reload, which
                        // is not what "the server is the state" promises. Nothing
                        // travels while the form is invalid: the API validates what it
                        // is given, and a rejected Back would strand them here.
                        ...(Object.keys(validateCompanyStep(values)).length
                          ? {}
                          : { business: buildCompanyPayload(values) }),
                      },
                      {
                        onError: (error) =>
                          setStatus(extractErrorMessage(error).message),
                      },
                    );
                  }}
                >
                  Back
                </Button>
                <Button
                  type="submit"
                  disabled={isSubmitting || saveMutation.isPending}
                  aria-busy={
                    isSubmitting || saveMutation.isPending || undefined
                  }
                >
                  {(isSubmitting || saveMutation.isPending) && (
                    <Spinner aria-hidden data-icon="inline-start" />
                  )}
                  Continue
                </Button>
              </Stack>
            </Stack>
          </Form>
        )}
      </Formik>
    </StepLayout>
  );
};
